/**
 * Źródła ciepła LOKALU edytowane z panelu (Bronek 2026-10-12: „Źródła Ciepła w lokalu – prośba
 * o wybranie źródeł ciepła z poziomu Admina"; kontrakt-API #416 pkt 3).
 *
 * Panel zapisuje źródła w TYM SAMYM kształcie co tablet (mobile InspectionFormScreen →
 * `HeatSourceRecord`): `{ id, type, heatCharacter, params }`, z charakterem także w `params`
 * (karta tabletu `HeatSourceForm` czyta go z params — bez tego pokazałaby default typu i przy pierwszej
 * edycji nadpisała wybór z panelu). Brakujące parametry uzupełniają defaulty Bronka: tablet przy
 * wyświetlaniu (HeatSourceForm: defaulty ⊕ value), bot przy wysyłce (fillCeebDraft.prepareHeatSourceData).
 *
 * Reguły formularza = LUSTRO tabletu (InspectionFormScreen: handleAddHeatSource /
 * activateExclusiveFlag / buildInspectionPayload):
 *  - „Brak źródła ciepła" wyklucza źródła: zaznaczenie czyści listę (po potwierdzeniu, gdy są źródła)
 *    i odznacza „podłączony do wspólnego";
 *  - „podłączony do wspólnego źródła budynku" (N3, właściciel 2026-09-13) NIE wyklucza własnych
 *    źródeł — odznacza tylko „brak źródła";
 *  - dodanie źródła odznacza „brak źródła", „podłączony" zostaje.
 *
 * Parytet list z tabletem: backend/test/heatSourceParamsParity.test.js.
 */
import { HEAT_TYPES_WITHOUT_CHARACTER } from '../ds/HeatSourceLabels';

/**
 * Typy źródeł do wyboru w LOKALU — LUSTRO `APARTMENT_HEAT_SOURCE_KEYS` z
 * mobile/src/screens/InspectionFormScreen.tsx (ta sama lista i kolejność; bez kolektorów słonecznych).
 */
export const APARTMENT_HEAT_SOURCE_TYPES: string[] = [
  'electric_heating',
  'gas_boiler',
  'solid_fuel_boiler',
  'fireplace',
  'tile_stove',
  'kitchen_stove',
  'heat_pump',
  'network',
  'photovoltaic',
  'kitchen_core',
  'oil_boiler',
];

/**
 * Dozwolone wartości „Charakteru produkowanego ciepła" per typ — LUSTRO opcji pola `heatCharacter`
 * w mobile/src/constants/heatSourceSchema.ts (HEAT_SOURCE_SCHEMA). Etykiety: ds/HeatSourceLabels
 * (HEAT_CHARACTER_LABELS). Typy bez tego pola w CEEB (HEAT_TYPES_WITHOUT_CHARACTER) mają listę pustą.
 */
export const HEAT_CHARACTER_KEYS_BY_TYPE: Record<string, string[]> = {
  gas_boiler: ['heating', 'hot_water', 'heating_and_hot_water', 'mop', 'funkcja_technologiczna'],
  solid_fuel_boiler: ['heating', 'hot_water', 'heating_and_hot_water'],
  oil_boiler: ['heating', 'hot_water', 'heating_and_hot_water'],
  solar_collectors: ['heating', 'hot_water', 'heating_and_hot_water'],
  fireplace: ['heating', 'hot_water', 'heating_and_hot_water', 'mop'],
  electric_heating: ['heating', 'hot_water', 'heating_and_hot_water'],
  tile_stove: [],
  kitchen_stove: [],
  heat_pump: ['heating', 'hot_water', 'heating_and_hot_water'],
  kitchen_core: [],
  network: ['heating', 'hot_water', 'heating_and_hot_water'],
  photovoltaic: [],
};

/** Źródło ciepła lokalu tak, jak leży w `apartment_inspection_records.heat_sources` (JSONB z tabletu). */
export type ApartmentHeatSource = Record<string, unknown> & {
  id?: unknown;
  type?: unknown;
  heatCharacter?: unknown;
  params?: unknown;
};

/** Stan części „Źródła ciepła" formularza lokalu. */
export interface ApartmentHeatState {
  /** Lista DOKŁADNIE taka, jak przyszła z serwera (nieznane pola i params zostają nietknięte). */
  heatSources: ApartmentHeatSource[];
  /**
   * Flagi TRÓJSTANOWE jak w bazie: null = nie wiadomo (np. rekord założony przez admina albo sprzed A2).
   * Bot rozróżnia null („nie dotykaj w CEEB") od false („odznacz wspólne w CEEB") —
   * backend utils/apartmentHeatFlags.planApartmentHeatFlags — więc null nie może sam zamienić się w false.
   */
  usesBuildingHeat: boolean | null;
  noHeatSource: boolean | null;
}

/** Które pola tej części admin zmienił — tylko one idą w zapisie (reszta: serwer zachowuje, COALESCE #57). */
export interface ApartmentHeatDirty {
  heatSources: boolean;
  usesBuildingHeat: boolean;
  noHeatSource: boolean;
}

export function isHeatDirty(d: ApartmentHeatDirty): boolean {
  return d.heatSources || d.usesBuildingHeat || d.noHeatSource;
}

/** Czy typ ma w CEEB pole „Charakter produkowanego ciepła". */
export function hasHeatCharacter(type: string): boolean {
  return !HEAT_TYPES_WITHOUT_CHARACTER.includes(type) && (HEAT_CHARACTER_KEYS_BY_TYPE[type] || []).length > 0;
}

/** Wpis listy da się pokazać (ma typ). Pozostałe wpisy zostają w danych bez zmian — nie gubimy ich przy zapisie. */
export function isDisplayableHeatSource(hs: unknown): hs is ApartmentHeatSource & { type: string } {
  return !!hs && typeof hs === 'object' && typeof (hs as ApartmentHeatSource).type === 'string'
    && (hs as ApartmentHeatSource).type !== '';
}

/** Stan z rekordu z serwera (kształt GET /apartment-inspections). Brak rekordu = pusta lista, flagi nieznane (null). */
export function heatStateFromRecord(record: {
  heatSources?: unknown;
  usesBuildingHeat?: boolean | null;
  noHeatSource?: boolean | null;
} | null | undefined): ApartmentHeatState {
  return {
    heatSources: Array.isArray(record?.heatSources) ? (record!.heatSources as ApartmentHeatSource[]) : [],
    usesBuildingHeat: typeof record?.usesBuildingHeat === 'boolean' ? record.usesBuildingHeat : null,
    noHeatSource: typeof record?.noHeatSource === 'boolean' ? record.noHeatSource : null,
  };
}

let idSeq = 0;
/** Id klienckie jak na tablecie (`aphs-…`); licznik — dwa dodania w tej samej milisekundzie nie zderzą się. */
function newHeatSourceId(): string {
  idSeq += 1;
  return `aphs-${Date.now()}-${idSeq}`;
}

/**
 * Nowe źródło lokalu w kształcie tabletu. Typ bez charakteru (piecokuchnia, trzon, kaflowy,
 * fotowoltaika) dostaje techniczne 'heating' tylko na górnym poziomie — jak tablet i panel budynku;
 * bot tego pola dla nich nie wysyła. `location` = klucz pomieszczenia (`params.lokalizacja`, opcjonalny).
 */
export function newApartmentHeatSource(type: string, heatCharacter: string, location?: string): ApartmentHeatSource {
  const withCharacter = hasHeatCharacter(type);
  const params: Record<string, unknown> = {};
  if (withCharacter) params.heatCharacter = heatCharacter;
  if (location) params.lokalizacja = location;
  return {
    id: newHeatSourceId(),
    type,
    heatCharacter: withCharacter ? heatCharacter : 'heating',
    params,
  };
}

/**
 * Odznaczenie flagi wykluczonej przez wybór admina: true → false, ale nieznana (null) zostaje nieznana —
 * dla bota null i false przy wykluczonej fladze znaczą to samo, a null nie jest zapisywany po cichu jako false.
 */
const untick = (v: boolean | null): boolean | null => (v === true ? false : v);

/** Dodanie źródła: lokal ma własne źródło → „brak źródła" odznaczony; „podłączony do wspólnego" zostaje (N3). */
export function addHeatSource(state: ApartmentHeatState, source: ApartmentHeatSource): ApartmentHeatState {
  return { ...state, heatSources: [...state.heatSources, source], noHeatSource: untick(state.noHeatSource) };
}

/** Usunięcie wpisu po POZYCJI w liście z serwera (wpisy bez id też da się usunąć); pozostałe obiekty bez zmian. */
export function removeHeatSourceAt(state: ApartmentHeatState, index: number): ApartmentHeatState {
  return { ...state, heatSources: state.heatSources.filter((_, i) => i !== index) };
}

/**
 * Edycja ISTNIEJĄCEGO źródła lokalu po POZYCJI w liście (zadanie 4, 2026-10-21 — przegląd z papieru wprowadzany
 * w panelu „jak na tablecie"): charakter + parametry z formularza tabletu (lustro utils/heatSourceParams).
 * `paramChanges` = WYŁĄCZNIE pola zmienione względem odczytu (heatParamChanges) — reszta `params` (także pola,
 * których panel nie zna) zostaje nietknięta. Charakter ląduje na górnym poziomie i w `params` (karta tabletu czyta
 * go z params — jak newApartmentHeatSource). Brak zmian = ten sam stan (lista nie robi się „zmieniona").
 */
export function updateHeatSourceAt(
  state: ApartmentHeatState,
  index: number,
  edit: { heatCharacter?: string; paramChanges?: Record<string, unknown> },
): ApartmentHeatState {
  const current = state.heatSources[index];
  if (!isDisplayableHeatSource(current)) return state;
  const params = current.params && typeof current.params === 'object' ? (current.params as Record<string, unknown>) : {};
  const changes = { ...(edit.paramChanges || {}) };
  const oldCharacter = String(current.heatCharacter ?? current.heat_character ?? params.heatCharacter ?? '');
  const withCharacter = hasHeatCharacter(current.type);
  const characterChanged = withCharacter && !!edit.heatCharacter && edit.heatCharacter !== oldCharacter;
  if (!characterChanged && Object.keys(changes).length === 0) return state;
  if (characterChanged) changes.heatCharacter = edit.heatCharacter;
  const next: ApartmentHeatSource = {
    ...current,
    ...(characterChanged ? { heatCharacter: edit.heatCharacter } : {}),
    params: { ...params, ...changes },
  };
  return { ...state, heatSources: state.heatSources.map((hs, i) => (i === index ? next : hs)) };
}

/** „Brak źródła ciepła" zaznaczony: wyklucza listę i „podłączony do wspólnego" (lustro activateExclusiveFlag). */
export function setNoHeatSource(state: ApartmentHeatState, value: boolean): ApartmentHeatState {
  if (!value) return { ...state, noHeatSource: false };
  return { heatSources: [], usesBuildingHeat: untick(state.usesBuildingHeat), noHeatSource: true };
}

/** „Podłączony do wspólnego źródła budynku" — odznacza tylko „brak źródła" (N3: własne źródła zostają). */
export function setUsesBuildingHeat(state: ApartmentHeatState, value: boolean): ApartmentHeatState {
  return value
    ? { ...state, usesBuildingHeat: true, noHeatSource: untick(state.noHeatSource) }
    : { ...state, usesBuildingHeat: false };
}

/**
 * Które pola admin zmienił względem stanu wczytanego z serwera. Lista: porównanie referencji (każda
 * zmiana listy tworzy nową tablicę; wczytana zostaje tą samą). Flaga przełączona tam i z powrotem = bez zmian.
 */
export function heatDirtyAgainst(loaded: ApartmentHeatState, current: ApartmentHeatState): ApartmentHeatDirty {
  return {
    heatSources: loaded.heatSources !== current.heatSources,
    usesBuildingHeat: loaded.usesBuildingHeat !== current.usesBuildingHeat,
    noHeatSource: loaded.noHeatSource !== current.noHeatSource,
  };
}

/**
 * Pola źródeł ciepła do body POST /apartment-inspections — WYŁĄCZNIE te, które admin zmienił względem
 * `loaded` (stanu wczytanego z serwera). Pominięte pole = serwer zachowuje swoją wartość (COALESCE,
 * wzorzec #57), więc dane z tabletu nie są nadpisywane po cichu. `heatSources` idzie zawsze CAŁĄ listą
 * (serwer podmienia tablicę), z obiektami wczytanymi bez zmian. „Brak źródła" maskuje listę jak tablet
 * (buildInspectionPayload).
 *
 * `newRecord` — rekordu tego przeglądu jeszcze nie ma (formularz pokazuje źródła z ostatniego przeglądu
 * lokalu): gdy admin cokolwiek w tej części zmienił, idzie lista, którą widział (nowy rekord nie dostanie
 * samej flagi bez źródeł), a flaga — tylko zmieniona albo ZNANA w poprzednim przeglądzie. Nieznana (null)
 * nie jest wysyłana jako false (bot: null = nie dotykaj w CEEB). Nie ma tu czego nadpisać — rekord
 * założony w międzyczasie przez tablet zatrzyma kontrola wersji (409).
 */
export function heatPayload(
  loaded: ApartmentHeatState,
  state: ApartmentHeatState,
  opts: { newRecord?: boolean } = {},
): { heatSources?: ApartmentHeatSource[]; usesBuildingHeat?: boolean; noHeatSource?: boolean } {
  const dirty = heatDirtyAgainst(loaded, state);
  const all = !!opts.newRecord && isHeatDirty(dirty);
  const out: { heatSources?: ApartmentHeatSource[]; usesBuildingHeat?: boolean; noHeatSource?: boolean } = {};
  if (all || dirty.heatSources) out.heatSources = state.noHeatSource === true ? [] : state.heatSources;
  const sendFlag = (changed: boolean, loadedValue: boolean | null) =>
    changed || (all && typeof loadedValue === 'boolean');
  if (sendFlag(dirty.usesBuildingHeat, loaded.usesBuildingHeat) && typeof state.usesBuildingHeat === 'boolean') {
    out.usesBuildingHeat = state.usesBuildingHeat;
  }
  if (sendFlag(dirty.noHeatSource, loaded.noHeatSource) && typeof state.noHeatSource === 'boolean') {
    out.noHeatSource = state.noHeatSource;
  }
  return out;
}
